// THE STATE OF A DEBT, NEVER OF A DOCUMENT.
//
// These rows used to render `<StatusBadge kind="invoice">`, which says "הופקה" — issued.
// That is the vocabulary of producing an invoice, and this screen has no such concept:
// the real invoice is created in each partner's own accounting software, and what this
// system holds is a record that it exists. Reading "הופקה" on a collections row invites
// exactly the question the rebuild removed — did we issue it yet? — instead of the one
// the screen is for: has the client paid.
//
// So the state is derived from MONEY ALONE — what is owed, what came in, and whether the
// due date has passed — and never from `invoice.status`. The four states are the only
// four a debt can be in:
//
//   נגבה במלואו   nothing left owed
//   באיחור        owed, and the client is past the due date
//   נגבה חלקית    some money in, a balance still open
//   פתוח לגבייה   billed, nothing in yet, not yet late
//
// Shared by the money screen's retainer and job cards AND the expense rows' bearer cell,
// so no two screens can drift apart on what a state means — the same reason
// InvoiceActions was extracted on the old screens. It moved up to components/ when the
// bearer cell became its second page: "חויב" on an expense row was being read as "paid".
import he from '../i18n/he';

const t = he.collections.board;

const SETTLED_EPSILON = 0.005;

export default function ChargeState({ row }) {
  if (!(row.balance > SETTLED_EPSILON)) {
    return <span className="badge green">{t.stateCollected}</span>;
  }
  // The client is late. This is the one word the old row and the new one share, and it
  // means something different here: not "the invoice is in an overdue status" but "the
  // money is late". It is what the collapsed retainer badge counts.
  if (row.overdue) return <span className="badge red">{t.stateOverdue}</span>;
  if (row.paid > 0) return <span className="badge amber">{t.statePartial}</span>;
  return <span className="badge blue">{t.stateOpen}</span>;
}

// A card's open money, SPLIT so the same shekels are never stated twice.
//
// A header used to say both "חיוב אחד באיחור" and "פתוח לגבייה: 1,000" about the same
// 1,000 — two lines, one debt, and a reader who adds them up is out by double. Overdue
// already means open to collect; it is the same money in a worse state. So the header
// reports the overdue amount on the overdue line, and "פתוח לגבייה" covers ONLY what is
// owed and not yet late. The two are disjoint by construction and sum to `outstanding`.
//
// All three are counted from the same rows the component above renders, so the badges
// and the table can never disagree.
const isOpen = (r) => r.kind === 'recorded' && r.balance > SETTLED_EPSILON;

export const overdueCount = (rows) => rows.filter((r) => isOpen(r) && r.overdue).length;

export const overdueTotal = (rows) =>
  rows.filter((r) => isOpen(r) && r.overdue).reduce((s, r) => s + r.balance, 0);

// Owed, but the client is not late yet. What is left of `outstanding` once the overdue
// part has been named — never the whole of it.
export const openNotOverdue = (rows) =>
  rows.filter((r) => isOpen(r) && !r.overdue).reduce((s, r) => s + r.balance, 0);
